import { Currency, CurrencyAmount } from '@pancakeswap/sdk'
import { Pool, Position } from '@pancakeswap/v3-sdk'
import { useMemo } from 'react'
import tryParseCurrencyAmount from 'utils/tryParseCurrencyAmount'
import { TokenPrices } from '../usePriceAPI'

/**
 * Helper function to get token price from the price API response
 * Maps token symbol to the corresponding price field
 */
function getTokenPrice(symbol: string, prices: TokenPrices): number | null {
  const symbolLower = symbol.toLowerCase()

  // Direct lookup from prices object
  if (symbolLower === 'wkas' || symbolLower === 'kas') {
    return prices.kaspa?.usd ?? null
  }

  // Try direct lookup using symbol
  const price = prices[symbolLower]
  if (typeof price?.usd === 'number') {
    return price.usd
  }

  // Assume stablecoins are $1
  if (symbolLower === 'usdc' || symbolLower === 'usdt' || symbolLower === 'dai' || symbolLower === 'busd') {
    return 1.0
  }

  return null
}

interface AutoSwapCalculationParams {
  enabled: boolean
  pool?: Pool | null
  tickLower?: number
  tickUpper?: number
  inputCurrency?: Currency
  inputAmount?: string
  outputCurrency?: Currency
  outOfRange?: boolean
  depositADisabled?: boolean
  depositBDisabled?: boolean
  currencyBalances?: {
    CURRENCY_A?: CurrencyAmount<Currency>
    CURRENCY_B?: CurrencyAmount<Currency>
  }
  tokenPrices?: TokenPrices | null
}

interface AutoSwapResult {
  needsSwap: boolean
  swapFromCurrency?: Currency
  swapToCurrency?: Currency
  swapAmount?: CurrencyAmount<Currency>
  finalDepositAmount0?: CurrencyAmount<Currency>
  finalDepositAmount1?: CurrencyAmount<Currency>
  token0Currency?: Currency
  token1Currency?: Currency
  swapPercentage?: number
  error?: string
}

/**
 * Calculates the optimal auto-swap amounts for V3 liquidity provision
 *
 * For a concentrated liquidity position, the ratio of token0 to token1 depends on:
 * - Current pool price
 * - Selected price range (tickLower to tickUpper)
 * - Position relative to current price
 *
 * This hook helps users add liquidity with a single token by auto-swapping to get the required counterpart.
 *
 * Strategy:
 * 1. User inputs desired deposit amount (e.g., 1 KAS)
 * 2. Calculate how much of the counterpart token is needed (e.g., 1698 NACHO for 1 KAS)
 * 3. Check if user has enough counterpart token in wallet
 * 4. If YES: Use input amount + required counterpart from balance (no swap needed)
 * 5. If NO: Calculate deficit and swap from OTHER wallet balance to fill the gap
 *
 * Example:
 * - Input: 1 KAS, Need: 1698 NACHO
 * - Wallet: 34 KAS, 300 NACHO
 * - Deficit: 1398 NACHO
 * - Swap: Use wallet KAS (not the input) to get 1398 NACHO
 * - Final: Deposit 1 KAS + 1698 NACHO (300 existing + 1398 from swap)
 */
export function useV3AutoSwapCalculation(params: AutoSwapCalculationParams): AutoSwapResult {
  const {
    enabled,
    pool,
    tickLower,
    tickUpper,
    inputCurrency,
    inputAmount,
    outputCurrency,
    outOfRange,
    depositADisabled,
    depositBDisabled,
    currencyBalances,
    tokenPrices,
  } = params

  return useMemo(() => {
    // If auto-swap is disabled, return early
    if (!enabled) {
      return { needsSwap: false }
    }

    // Validate inputs
    if (!pool || !inputCurrency || !outputCurrency || !inputAmount) {
      return { needsSwap: false, error: '' }
    }

    if (typeof tickLower !== 'number' || typeof tickUpper !== 'number') {
      return { needsSwap: false, error: 'Invalid tick range' }
    }

    // Parse input amount
    const parsedInputAmount = tryParseCurrencyAmount(inputAmount, inputCurrency)
    if (!parsedInputAmount) {
      return { needsSwap: false, error: 'Invalid input amount' }
    }

    // If position is entirely out of range (single-sided deposit), no swap needed
    if (depositADisabled || depositBDisabled) {
      return {
        needsSwap: false,
        error: 'Position is out of range - single token deposit only',
      }
    }

    try {
      const { token0, token1 } = pool
      const isInputToken0 = inputCurrency.wrapped.equals(token0)

      // Check if position is in range
      const currentTick = pool.tickCurrent
      if (currentTick < tickLower) {
        return { needsSwap: false, error: 'Price below range - only token0 needed' }
      }
      if (currentTick >= tickUpper) {
        return { needsSwap: false, error: 'Price above range - only token1 needed' }
      }

      // Use V3 SDK Position to calculate optimal ratio
      // Create a hypothetical position with equal amounts to determine the ratio
      const hypotheticalPosition = Position.fromAmounts({
        pool,
        tickLower,
        tickUpper,
        amount0: BigInt(10) ** BigInt(token0.decimals), // 1 unit of token0
        amount1: BigInt(10) ** BigInt(token1.decimals), // 1 unit of token1
        useFullPrecision: true,
      })

      // Extract the actual amounts the position would use
      // This gives us the optimal ratio for the selected range
      const ratio0 = hypotheticalPosition.amount0.quotient
      const ratio1 = hypotheticalPosition.amount1.quotient

      // Get user's current balances - MUST map to token0/token1, not CURRENCY_A/B
      // because token0/token1 are sorted by the pool, but CURRENCY_A/B are based on user input order
      const currencyA = currencyBalances?.CURRENCY_A?.currency
      const currencyB = currencyBalances?.CURRENCY_B?.currency

      const isAToken0 = currencyA?.wrapped.equals(token0)
      const balance0 = isAToken0
        ? currencyBalances?.CURRENCY_A?.quotient ?? BigInt(0)
        : currencyBalances?.CURRENCY_B?.quotient ?? BigInt(0)
      const balance1 = isAToken0
        ? currencyBalances?.CURRENCY_B?.quotient ?? BigInt(0)
        : currencyBalances?.CURRENCY_A?.quotient ?? BigInt(0)

      // User's input amount (what they want to deposit)
      const inputToken0 = isInputToken0 ? parsedInputAmount.quotient : BigInt(0)
      const inputToken1 = !isInputToken0 ? parsedInputAmount.quotient : BigInt(0)

      if (isInputToken0) {
        // User wants to deposit inputToken0 (e.g., 1 KAS)
        // Calculate how much token1 is needed to pair with it
        // Using ratio: token1Amount = (token0Amount * ratio1) / ratio0
        const neededToken1 = (inputToken0 * ratio1) / ratio0

        // Check if user has enough token1 in wallet
        if (balance1 >= neededToken1) {
          // ✅ User has sufficient token1 balance - NO SWAP NEEDED
          // Use the input amount + required token1 from existing balance
          return {
            needsSwap: false,
            swapFromCurrency: inputCurrency,
            swapToCurrency: outputCurrency,
            swapAmount: CurrencyAmount.fromRawAmount(inputCurrency, BigInt(0)),
            finalDepositAmount0: CurrencyAmount.fromRawAmount(token0, inputToken0), // Use token0 (wrapped)
            finalDepositAmount1: CurrencyAmount.fromRawAmount(token1, neededToken1), // Use token1 (wrapped)
            token0Currency: token0,
            token1Currency: token1,
            swapPercentage: 0,
          }
        }

        // ❌ User doesn't have enough token1
        // Need to swap from OTHER balance to get the deficit

        if (!tokenPrices) {
          return {
            needsSwap: false,
            error: 'Token prices not available for swap calculation',
          }
        }

        let token0Price = getTokenPrice(token0.symbol?.toLowerCase() || '', tokenPrices)
        let token1Price = getTokenPrice(token1.symbol?.toLowerCase() || '', tokenPrices)

        // Fallback: If one price is missing, calculate it from the pool price and the other token's price
        if (!token0Price || !token1Price) {
          // pool.token0Price = how many token1 per 1 token0 (e.g., WKAS per KENOBI)
          // pool.token1Price = how many token0 per 1 token1 (e.g., KENOBI per WKAS)
          const token1PerToken0 = parseFloat(pool.token0Price.toSignificant(18))
          const token0PerToken1 = parseFloat(pool.token1Price.toSignificant(18))
          if (token0Price && !token1Price) {
            // We know token0 USD price, need to calculate token1 USD price
            // If 1 token0 = X token1, then token1Price = token0Price / X
            token1Price = token0Price / token1PerToken0
          } else if (token1Price && !token0Price) {
            // We know token1 USD price, need to calculate token0 USD price
            // If 1 token1 = X token0, then token0Price = token1Price / X
            token0Price = token1Price / token0PerToken1
          } else {
            // Both prices are missing - cannot proceed
            return {
              needsSwap: false,
              error: `Prices not available for both ${token0.symbol} and ${token1.symbol}`,
            }
          }
        }

        // Calculate the deficit
        const token1Deficit = neededToken1 - balance1

        // Convert deficit to USD value
        const token1DeficitValueUSD = (Number(token1Deficit) / 10 ** token1.decimals) * token1Price

        // Calculate how much token0 we need to swap to get this USD value
        // This comes from OTHER wallet balance, NOT from the input amount
        const token0ToSwapAmount = (token1DeficitValueUSD / token0Price) * 10 ** token0.decimals

        // Safety check: ensure we have a valid number before converting to BigInt
        if (!Number.isFinite(token0ToSwapAmount) || token0ToSwapAmount < 0) {
          console.error('Invalid swap amount:', { token0ToSwapAmount, token1DeficitValueUSD, token0Price })
          return {
            needsSwap: false,
            error: 'Invalid swap amount calculation',
          }
        }

        const token0ToSwapRaw = BigInt(Math.floor(token0ToSwapAmount))

        // Check if user has enough OTHER token0 balance to perform the swap
        // (balance0 should be >= inputToken0 + token0ToSwapRaw)
        const totalToken0Needed = inputToken0 + token0ToSwapRaw

        if (balance0 < totalToken0Needed) {
          return {
            needsSwap: false,
            error: `Insufficient ${token0.symbol} balance. Need ${Number(totalToken0Needed) / 10 ** token0.decimals} ${token0.symbol
              } total (${Number(inputToken0) / 10 ** token0.decimals} to deposit + ${Number(token0ToSwapRaw) / 10 ** token0.decimals
              } to swap), but only have ${Number(balance0) / 10 ** token0.decimals} ${token0.symbol}`,
          }
        }

        // Calculate how much token1 we'll get from the swap
        const token1FromSwapAmount =
          (((Number(token0ToSwapRaw) / 10 ** token0.decimals) * token0Price) / token1Price) * 10 ** token1.decimals

        if (!Number.isFinite(token1FromSwapAmount) || token1FromSwapAmount < 0) {
          console.error('Invalid token1 from swap:', { token1FromSwapAmount })
          return {
            needsSwap: false,
            error: 'Invalid swap result calculation',
          }
        }

        const token1FromSwap = BigInt(Math.floor(token1FromSwapAmount))

        // Final amounts after swap
        const finalToken1 = balance1 + token1FromSwap

        return {
          needsSwap: true,
          swapFromCurrency: inputCurrency, // Swapping from token0
          swapToCurrency: outputCurrency, // To token1
          swapAmount: CurrencyAmount.fromRawAmount(inputCurrency, token0ToSwapRaw),
          finalDepositAmount0: CurrencyAmount.fromRawAmount(token0, inputToken0), // Use token0 (wrapped), not inputCurrency
          finalDepositAmount1: CurrencyAmount.fromRawAmount(token1, finalToken1),
          token0Currency: token0,
          token1Currency: token1,
          swapPercentage: (Number(token0ToSwapRaw) / Number(totalToken0Needed)) * 100,
        }
      }

      // User wants to deposit inputToken1 (e.g., 1698 NACHO)
      // Calculate how much token0 is needed to pair with it
      // Using ratio: token0Amount = (token1Amount * ratio0) / ratio1
      const neededToken0 = (inputToken1 * ratio0) / ratio1

      // Check if user has enough token0 in wallet
      if (balance0 >= neededToken0) {
        // ✅ User has sufficient token0 balance - NO SWAP NEEDED
        return {
          needsSwap: false,
          swapFromCurrency: inputCurrency,
          swapToCurrency: outputCurrency,
          swapAmount: CurrencyAmount.fromRawAmount(inputCurrency, BigInt(0)),
          finalDepositAmount0: CurrencyAmount.fromRawAmount(token0, neededToken0), // Use token0 (wrapped)
          finalDepositAmount1: CurrencyAmount.fromRawAmount(token1, inputToken1), // Use token1 (wrapped)
          token0Currency: token0,
          token1Currency: token1,
          swapPercentage: 0,
        }
      }

      // ❌ User doesn't have enough token0
      // Need to swap from OTHER token1 balance to get the deficit

      if (!tokenPrices) {
        return {
          needsSwap: false,
          error: 'Token prices not available for swap calculation',
        }
      }

      let token0Price = getTokenPrice(token0.symbol?.toLowerCase() || '', tokenPrices)
      let token1Price = getTokenPrice(token1.symbol?.toLowerCase() || '', tokenPrices)

      // Fallback: If one price is missing, calculate it from the pool price and the other token's price
      if (!token0Price || !token1Price) {
        // pool.token0Price = how many token1 per 1 token0 (e.g., WKAS per KENOBI)
        // pool.token1Price = how many token0 per 1 token1 (e.g., KENOBI per WKAS)
        const token1PerToken0 = parseFloat(pool.token0Price.toSignificant(18))
        const token0PerToken1 = parseFloat(pool.token1Price.toSignificant(18))

        if (token0Price && !token1Price) {
          // We know token0 USD price, need to calculate token1 USD price
          // If 1 token0 = X token1, and token0 = $Y, then token1 = $Y / X
          // Example: 1 KENOBI = 0.5424 WKAS, KENOBI = $0.0001307
          // Then WKAS = $0.0001307 / 0.5424 = $0.000241
          // But actually: 1 KENOBI = 0.5424 WKAS means WKAS is worth less
          // So: token1Price = token0Price / token1PerToken0
          token1Price = token0Price / token1PerToken0
        } else if (token1Price && !token0Price) {
          // We know token1 USD price, need to calculate token0 USD price
          // If 1 token0 = X token1, and token1 = $Y, then token0 = $Y * X
          // Example: 1 KENOBI = 0.5424 WKAS, WKAS = $0.059421
          // Then KENOBI = $0.059421 / (1/0.5424) = $0.059421 * 0.5424... NO!
          // 1 KENOBI = 0.5424 WKAS means to get 1 KENOBI you need to pay 0.5424 WKAS
          // So if WKAS = $0.059421, then KENOBI = 0.5424 × $0.059421... NO!
          // Actually: if 1 KENOBI = 0.5424 WKAS, it means KENOBI is worth LESS than WKAS
          // Price relationship: token0_usd = token1_usd × token1PerToken0
          // But that gives us: 0.059421 × 0.5424 = 0.0322 (too high)
          // Let's think differently: pool.token1Price = KENOBI per WKAS
          // So: 1 WKAS = token0PerToken1 KENOBI
          // Therefore: KENOBI_usd = WKAS_usd / token0PerToken1
          token0Price = token1Price / token0PerToken1
        } else {
          // Both prices are missing - cannot proceed
          return {
            needsSwap: false,
            error: `Prices not available for both ${token0.symbol} and ${token1.symbol}`,
          }
        }
      }

      // Calculate the deficit
      const token0Deficit = neededToken0 - balance0

      // Convert deficit to USD value
      const token0DeficitValueUSD = (Number(token0Deficit) / 10 ** token0.decimals) * token0Price

      // Calculate how much token1 we need to swap to get this USD value
      const token1ToSwapAmount = (token0DeficitValueUSD / token1Price) * 10 ** token1.decimals

      // Safety check: ensure we have a valid number before converting to BigInt
      if (!Number.isFinite(token1ToSwapAmount) || token1ToSwapAmount < 0) {
        return {
          needsSwap: false,
          error: 'Invalid swap amount calculation',
        }
      }

      const token1ToSwapRaw = BigInt(Math.floor(token1ToSwapAmount))

      // Check if user has enough OTHER token1 balance to perform the swap
      const totalToken1Needed = inputToken1 + token1ToSwapRaw

      // Calculate how much token0 we'll get from the swap
      const token0FromSwapAmount =
        (((Number(token1ToSwapRaw) / 10 ** token1.decimals) * token1Price) / token0Price) * 10 ** token0.decimals

      if (!Number.isFinite(token0FromSwapAmount) || token0FromSwapAmount < 0) {
        return {
          needsSwap: false,
          error: 'Invalid swap result calculation',
        }
      }

      const token0FromSwap = BigInt(Math.floor(token0FromSwapAmount))

      // Check if user has enough balance (but don't block the swap - let them try)
      const hasInsufficientBalance = balance1 < totalToken1Needed
      const errorMessage = hasInsufficientBalance
        ? `Insufficient ${token1.symbol} balance. Need ${Number(totalToken1Needed) / 10 ** token1.decimals} ${token1.symbol
        } total (${Number(inputToken1) / 10 ** token1.decimals} to deposit + ${Number(token1ToSwapRaw) / 10 ** token1.decimals
        } to swap), but only have ${Number(balance1) / 10 ** token1.decimals} ${token1.symbol}`
        : undefined

      // Final amounts after swap
      const finalToken0 = balance0 + token0FromSwap

      return {
        needsSwap: true,
        swapFromCurrency: inputCurrency, // Swapping from token1
        swapToCurrency: outputCurrency, // To token0
        swapAmount: CurrencyAmount.fromRawAmount(inputCurrency, token1ToSwapRaw),
        finalDepositAmount0: CurrencyAmount.fromRawAmount(token0, finalToken0),
        finalDepositAmount1: CurrencyAmount.fromRawAmount(token1, inputToken1), // Use token1 (wrapped), not inputCurrency
        token0Currency: token0,
        token1Currency: token1,
        swapPercentage: (Number(token1ToSwapRaw) / Number(totalToken1Needed)) * 100,
        error: errorMessage, // Include warning if insufficient balance
      }
    } catch (error) {
      console.error('Error calculating auto-swap amounts:', error)
      return {
        needsSwap: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }, [
    enabled,
    pool,
    tickLower,
    tickUpper,
    inputCurrency,
    inputAmount,
    outputCurrency,
    depositADisabled,
    depositBDisabled,
    currencyBalances,
    tokenPrices,
  ])
}
